import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles } from 'lucide-react';
import { useCurrentRelease } from '../hooks/useCurrentRelease';
import { useDialogA11y } from '@/shared/hooks/useDialogA11y';
import { pickChangelog } from '@shared/schemas/releases';

const LAST_SEEN_KEY = '__last_seen_version';

export function VersionModal() {
  const { t, i18n } = useTranslation();
  const { data: release } = useCurrentRelease();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!release) return;
    const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
    // Solo mostrar si el user NO ha visto ESTA versión. Si es la primera vez
    // (lastSeen null), marcamos como vista SIN mostrar el modal — evita
    // spamear a usuarios que instalan hoy con la última release ya publicada.
    if (lastSeen === null) {
      localStorage.setItem(LAST_SEEN_KEY, release.version);
      return;
    }
    if (lastSeen !== release.version) {
      setOpen(true);
    }
  }, [release]);

  const dismiss = () => {
    if (release) localStorage.setItem(LAST_SEEN_KEY, release.version);
    setOpen(false);
  };
  const panelRef = useDialogA11y(dismiss, open);

  if (!release || !open) return null;

  const bullets = pickChangelog(release.changelog, i18n.language);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsnew-title"
      onClick={dismiss}
    >
      <div
        ref={panelRef}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-brand" size={20} />
            <h2 id="whatsnew-title" className="text-lg font-semibold">
              {t('releases.whatsnew', { defaultValue: 'Novedades' })}
              <span className="ml-2 text-sm font-normal text-muted">v{release.version}</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="text-muted hover:text-ink"
            aria-label={t('common.close', { defaultValue: 'Cerrar' })}
          >
            <X size={20} />
          </button>
        </div>
        {bullets.length > 0 ? (
          <ul className="space-y-2 text-sm text-ink">
            {bullets.map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-brand">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">
            {t('releases.no_details', { defaultValue: 'Sin detalles.' })}
          </p>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="mt-6 w-full rounded-lg bg-brand py-2.5 text-sm font-medium text-white hover:bg-brand-700"
        >
          {t('releases.dismiss', { defaultValue: 'Entendido' })}
        </button>
      </div>
    </div>
  );
}
